/**
 * Os onze controles, e a decisão de qual aparece.
 *
 * Todos recebem e devolvem `Valor` — o que o banco grava — e nenhum guarda
 * estado próprio: quem guarda o rascunho é `Pergunta`, e é isso que faz a
 * resposta digitada sobreviver a uma falha de rede sem nada de especial.
 *
 * `numero` é `type="text"` com teclado decimal, e não `type="number"`, por dois
 * motivos concretos: em pt-BR se digita "3,5", e um `input[type=number]`
 * descarta a vírgula assim que ela é escrita — o convidado vê o que digitou
 * sumir. O motor (`valor_numero`) já entende vírgula, então recusá-la aqui
 * seria inventar uma regra que o banco não tem.
 */

import type { NoDoConvite, Valor } from '../../tipos';
import { degrausDaEscala, formatarTelefone, lerLimites, SIM_NAO } from './valores';

export type PropsDoControle = {
  readonly no: NoDoConvite;
  readonly valor: Valor;
  readonly aoMudar: (valor: Valor) => void;
  readonly invalido: boolean;
  /** O `id` do rótulo da pergunta: é ele que nomeia o campo. */
  readonly idDoRotulo: string;
  readonly idDaDescricao: string | undefined;
};

function texto(valor: Valor): string {
  if (valor === null || valor === undefined) return '';
  if (Array.isArray(valor)) return '';
  return String(valor);
}

function lista(valor: Valor): readonly string[] {
  return Array.isArray(valor) ? valor : [];
}

export function Controle(props: PropsDoControle) {
  switch (props.no.subtipo) {
    case 'texto_longo':
      return <TextoLongo {...props} />;
    case 'escolha_unica':
      return <Escolha {...props} multipla={false} opcoes={props.no.opcoes} />;
    case 'escolha_multipla':
      return <Escolha {...props} multipla opcoes={props.no.opcoes} />;
    case 'sim_nao':
      // Se o nó trouxe opções, são ELAS que vão — caixa e acento inclusive. O
      // banco compara a string gravada com a lista dele, e normalizar aqui faria
      // o convidado receber uma recusa que ele não consegue resolver clicando.
      // As nossas duas só entram quando não veio nada.
      return (
        <Escolha
          {...props}
          multipla={false}
          opcoes={props.no.opcoes.length > 0 ? props.no.opcoes : SIM_NAO.map((o) => o.valor)}
          rotulos
        />
      );
    case 'escala':
      return <Escala {...props} />;
    default:
      return <Linha {...props} />;
  }
}

/* ── Campo de uma linha: texto_curto, numero, data, hora, email, telefone ─── */

type Teclado = {
  readonly tipo: 'text' | 'email' | 'tel' | 'date' | 'time';
  readonly modo?: 'text' | 'decimal' | 'email' | 'tel';
  readonly completar?: string;
};

const TECLADOS: Record<string, Teclado> = {
  numero: { tipo: 'text', modo: 'decimal' },
  email: { tipo: 'email', modo: 'email', completar: 'email' },
  telefone: { tipo: 'tel', modo: 'tel', completar: 'tel' },
  data: { tipo: 'date' },
  hora: { tipo: 'time' },
};

function Linha({ no, valor, aoMudar, invalido, idDoRotulo, idDaDescricao }: PropsDoControle) {
  const limites = lerLimites(no);
  const teclado: Teclado = TECLADOS[no.subtipo ?? ''] ?? { tipo: 'text' };
  const telefone = no.subtipo === 'telefone' || limites.mascara === 'telefone';

  return (
    <input
      className="campo__controle"
      type={telefone ? 'tel' : teclado.tipo}
      inputMode={telefone ? 'tel' : teclado.modo}
      autoComplete={teclado.completar}
      value={texto(valor)}
      aria-labelledby={idDoRotulo}
      aria-describedby={idDaDescricao}
      aria-invalid={invalido || undefined}
      maxLength={
        no.subtipo === 'texto_curto' && limites.maximo && limites.maximo > 0
          ? Math.round(limites.maximo)
          : undefined
      }
      onChange={(evento) => {
        const bruto = evento.target.value;
        aoMudar(telefone ? formatarTelefone(bruto) : bruto);
      }}
    />
  );
}

function TextoLongo({ no, valor, aoMudar, invalido, idDoRotulo, idDaDescricao }: PropsDoControle) {
  const limites = lerLimites(no);
  return (
    <textarea
      className="campo__controle campo__controle--longo"
      rows={4}
      value={texto(valor)}
      aria-labelledby={idDoRotulo}
      aria-describedby={idDaDescricao}
      aria-invalid={invalido || undefined}
      maxLength={limites.maximo && limites.maximo > 0 ? Math.round(limites.maximo) : undefined}
      onChange={(evento) => aoMudar(evento.target.value)}
    />
  );
}

/* ── Escolha: única, múltipla e sim/não ──────────────────────────────────── */

type PropsDaEscolha = PropsDoControle & {
  readonly multipla: boolean;
  readonly opcoes: readonly string[];
  /** `sim_nao` grava `sim`/`nao` e mostra `Sim`/`Não`. */
  readonly rotulos?: boolean;
};

function Escolha({
  no,
  valor,
  aoMudar,
  invalido,
  idDoRotulo,
  idDaDescricao,
  multipla,
  opcoes,
  rotulos,
}: PropsDaEscolha) {
  const marcadas = multipla ? lista(valor) : [texto(valor)];

  // Opção não é vazio que se resolve na tela: uma `escolha_unica` sem opções é
  // grafo que `validar_grafo` recusa publicar. Se chegou aqui, o formulário foi
  // publicado antes dessa regra existir — e dizer isso é melhor do que mostrar
  // uma pergunta sem resposta possível.
  if (opcoes.length === 0) {
    return (
      <p className="aviso aviso--atencao" role="status">
        <span className="aviso__rotulo">Sem opções</span>
        <span className="aviso__corpo">
          Esta pergunta foi publicada sem alternativas. Avise quem enviou o convite.
        </span>
      </p>
    );
  }

  return (
    <fieldset
      className="opcoes"
      aria-labelledby={idDoRotulo}
      aria-describedby={idDaDescricao}
      aria-invalid={invalido || undefined}
    >
      {opcoes.map((opcao) => {
        const marcada = marcadas.includes(opcao);
        const visivel = rotulos ? (SIM_NAO.find((o) => o.valor === opcao)?.rotulo ?? opcao) : opcao;
        return (
          <label className="opcao" key={opcao}>
            <input
              className="opcao__marca"
              type={multipla ? 'checkbox' : 'radio'}
              name={no.id}
              value={opcao}
              checked={marcada}
              onChange={() => {
                if (!multipla) {
                  aoMudar(opcao);
                  return;
                }
                // A lista guarda a ordem de MARCAÇÃO, não a das opções: é
                // nessa ordem que a trilha devolve a resposta para conferência.
                const atuais = lista(valor);
                aoMudar(marcada ? atuais.filter((v) => v !== opcao) : [...atuais, opcao]);
              }}
            />
            <span className="opcao__texto">{visivel}</span>
          </label>
        );
      })}
    </fieldset>
  );
}

/* ── Escala ──────────────────────────────────────────────────────────────── */

function Escala({ no, valor, aoMudar, invalido, idDoRotulo, idDaDescricao }: PropsDoControle) {
  const degraus = degrausDaEscala(no);
  const marcado = typeof valor === 'number' ? valor : Number(texto(valor));

  return (
    <fieldset
      className="escala"
      aria-labelledby={idDoRotulo}
      aria-describedby={idDaDescricao}
      aria-invalid={invalido || undefined}
    >
      {degraus.map((degrau) => (
        <label className="escala__degrau" key={degrau}>
          <input
            className="so-leitor"
            type="radio"
            name={no.id}
            value={degrau}
            checked={marcado === degrau}
            onChange={() => aoMudar(degrau)}
          />
          <span className="escala__numero" data-numerico>
            {degrau}
          </span>
        </label>
      ))}
    </fieldset>
  );
}
